import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { horsePaymentsAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import RowActionsMenu from './RowActionsMenu';
import ConfirmDialog from './ConfirmDialog';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';

const emptyForm = { payment_type: 'deposit', payment_date: '', amount: '', notes: '' };

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '-';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(numeric);
};

// One section of the payment ledger — either what the buyer owes us (direction="sale",
// balanced against sale_amount) or what we owe the seller (direction="purchase",
// balanced against purchase_amount). Both directions share the same shape, so this
// renders whichever one it's given.
const PaymentSection = ({ title, direction, totalAmount, totalPaid, balanceDue, payments, onAdd, onEdit, onDelete }) => {
  const paidInFull = totalAmount !== null && totalAmount !== undefined && balanceDue <= 0;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1">{title}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="textSecondary">
                Paid {formatCurrency(totalPaid)} of {formatCurrency(totalAmount)}
              </Typography>
              {paidInFull ? (
                <Chip size="small" color="success" label="Paid in full" />
              ) : (
                <Chip size="small" color="warning" label={`${formatCurrency(balanceDue)} still owed`} />
              )}
            </Stack>
          </Box>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAdd}>
            Add Payment
          </Button>
        </Box>

        {payments.length === 0 ? (
          <Typography color="textSecondary">No {direction} payments recorded</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell align="center" sx={actionsKabobCellSx}>
                      <RowActionsMenu
                        actions={[
                          { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(p) },
                          { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => onDelete(p.id), color: 'error' },
                        ]}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{p.payment_type}</TableCell>
                    <TableCell>{formatDate(p.payment_date) ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                    <TableCell>{p.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

const HorsePayments = ({ horseId, horse, onChange }) => {
  const notify = useNotify();
  const [payments, setPayments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDirection, setDialogDirection] = useState('sale');
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadPayments = useCallback(async () => {
    try {
      const response = await horsePaymentsAPI.getAll(horseId);
      setPayments(toArray(response.data, 'horse payments'));
    } catch (error) {
      console.error('Error loading horse payments:', error);
    }
  }, [horseId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleOpenDialog = (direction, payment = null) => {
    setDialogDirection(direction);
    if (payment) {
      setEditingPayment(payment);
      setFormData({
        payment_type: payment.payment_type,
        payment_date: payment.payment_date || '',
        amount: payment.amount ?? '',
        notes: payment.notes || '',
      });
    } else {
      setEditingPayment(null);
      const hasExisting = payments.some((p) => p.direction === direction);
      setFormData({ ...emptyForm, payment_type: hasExisting ? 'payment' : 'deposit' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPayment(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingPayment) {
        await horsePaymentsAPI.update(horseId, editingPayment.id, formData);
      } else {
        await horsePaymentsAPI.create(horseId, { ...formData, direction: dialogDirection });
      }
      await loadPayments();
      onChange?.();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving horse payment:', error);
      notify.error(`Error saving payment: ${errorMessage(error, error.message)}`);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await horsePaymentsAPI.delete(horseId, deleteTarget);
      setDeleteTarget(null);
      await loadPayments();
      onChange?.();
    } catch (error) {
      console.error('Error deleting horse payment:', error);
      notify.error(errorMessage(error, 'Error deleting payment'));
    }
  };

  const purchaseAmountSet = horse?.purchase_amount !== null && horse?.purchase_amount !== undefined;
  const saleAmountSet = horse?.sale_amount !== null && horse?.sale_amount !== undefined;

  if (!purchaseAmountSet && !saleAmountSet) {
    return (
      <Typography color="textSecondary">
        Set a Purchase Amount or Sale Amount on this horse to start tracking payments.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {purchaseAmountSet && (
        <PaymentSection
          title="Purchase Payments"
          direction="purchase"
          totalAmount={horse.purchase_amount}
          totalPaid={horse.total_paid_purchase ?? 0}
          balanceDue={horse.balance_due_purchase ?? horse.purchase_amount}
          payments={payments.filter((p) => p.direction === 'purchase')}
          onAdd={() => handleOpenDialog('purchase')}
          onEdit={(p) => handleOpenDialog('purchase', p)}
          onDelete={setDeleteTarget}
        />
      )}

      {saleAmountSet && (
        <PaymentSection
          title="Sale Payments"
          direction="sale"
          totalAmount={horse.sale_amount}
          totalPaid={horse.total_paid_sale ?? 0}
          balanceDue={horse.balance_due_sale ?? horse.sale_amount}
          payments={payments.filter((p) => p.direction === 'sale')}
          onAdd={() => handleOpenDialog('sale')}
          onEdit={(p) => handleOpenDialog('sale', p)}
          onDelete={setDeleteTarget}
        />
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPayment ? 'Edit Payment' : `Add ${dialogDirection === 'sale' ? 'Sale' : 'Purchase'} Payment`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="Type"
              value={formData.payment_type}
              onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
              fullWidth
            >
              <MenuItem value="deposit">Deposit</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
            </TextField>

            <TextField
              size="small"
              label="Date"
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />

            <TextField
              size="small"
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              inputProps={{ step: '0.01' }}
              fullWidth
              required
            />

            <TextField
              size="small"
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.payment_date || !formData.amount}
          >
            {editingPayment ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Payment"
        message="Permanently delete this payment record? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </Stack>
  );
};

export default HorsePayments;
